/*
 * @Author: Ryan
 * @Date: 2025-12-25
 * @Description: Hero section for AI Avatar Generator landing page
 */
import React, { useRef, useState } from 'react';

import StartButton from './StartButton';

const PlayButton: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
  >
    <rect x="1" y="1" width="62" height="62" rx="31" fill="white" fillOpacity="0.1" />
    <rect x="1" y="1" width="62" height="62" rx="31" stroke="white" strokeWidth="2" />
    <path
      d="M41.5038 30.3359C42.6913 31.1275 42.6913 32.8725 41.5038 33.6641L29.1094 41.9271C27.7803 42.8131 26 41.8603 26 40.2629L26 23.7371C26 22.1397 27.7803 21.1869 29.1094 22.0729L41.5038 30.3359Z"
      fill="white"
    />
  </svg>
);

interface VideoData {
  posterUrl: string;
  videoUrl: string;
}

const videos: VideoData[] = [
  {
    posterUrl:
      'https://assets.falcocut.ai/landing-page/ai-avatar-generator/ai-avatar-hero-male-casual.png',
    videoUrl:
      'https://assets.falcocut.ai/landing-page/ai-avatar-generator/professionalvirtualspokespersonavatarvideo_1787467462892883968.mp4',
  },
  {
    posterUrl:
      'https://assets.falcocut.ai/landing-page/ai-avatar-generator/ai-avatar-hero-female-business.png',
    videoUrl:
      'https://assets.falcocut.ai/landing-page/ai-avatar-generator/gamingandstreamingavatarvideo_1787467337940377600.mp4',
  },
  {
    posterUrl:
      'https://assets.falcocut.ai/landing-page/ai-avatar-generator/ai-avatar-hero-female-product.png',
    videoUrl:
      'https://assets.falcocut.ai/landing-page/ai-avatar-generator/productdemoandpromotionavatar_1787467337931988992.mp4',
  },
];

const HeroSection: React.FC = () => {
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  const handlePlayClick = (index: number) => {
    // 暂停其他视频
    videoRefs.current.forEach((video, i) => {
      if (video && i !== index) {
        video.pause();
        video.currentTime = 0;
      }
    });

    const video = videoRefs.current[index];
    if (video) {
      video.play();
      setPlayingIndex(index);
    }
  };

  const handleVideoClick = (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      video.pause();
      video.currentTime = 0;
      setPlayingIndex(null);
    }
  };

  const handleVideoEnded = (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      video.currentTime = 0;
    }
    if (playingIndex === index) {
      setPlayingIndex(null);
    }
  };

  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题 */}
        <h1 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Your Realistic Avatar for Videos, Presentations, and Ads
        </h1>

        {/* 描述 */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-8 pad:mb-10 sm:mb-12 leading-relaxed">
          FalcoCut AI Avatar Generator turns text into lifelike digital presenters. Each
          avatar speaks with synced voice, expressions, and gestures, giving your videos a
          human touch. Whether you need a virtual spokesperson for ads, a talking head for
          e-commerce, or a trainer for online learning, FalcoCut makes it simple to create
          professional, engaging content in minutes—without cameras, actors, or studios.
        </p>

        {/* 视频卡片区域 */}
        <div className="relative mb-10 pad:mb-12 sm:mb-14">
          {/* 背景装饰 */}
          <div
            className="absolute inset-x-0 top-[10%] bottom-[10%] rounded-[32px]"
            style={{
              background:
                'linear-gradient(135deg, rgba(147, 197, 253, 0.3) 0%, rgba(196, 181, 253, 0.3) 50%, rgba(167, 243, 208, 0.3) 100%)',
            }}
          />

          {/* 视频卡片网格 */}
          <div className="relative flex flex-col pad:flex-row items-center justify-center gap-4 pad:gap-6 sm:gap-8 px-4 pad:px-8 sm:px-12 py-8 pad:py-10 sm:py-12">
            {videos.map((video, index) => (
              <div
                key={index}
                className="relative rounded-[20px] overflow-hidden bg-white shadow-lg w-[340px] h-[340px]"
              >
                <video
                  ref={(el) => {
                    videoRefs.current[index] = el;
                  }}
                  src={video.videoUrl}
                  poster={video.posterUrl}
                  className="w-full h-full object-cover cursor-pointer"
                  onEnded={() => handleVideoEnded(index)}
                  onClick={() => handleVideoClick(index)}
                  playsInline
                />
                {/* 播放按钮 */}
                {playingIndex !== index && (
                  <button
                    type="button"
                    onClick={() => handlePlayClick(index)}
                    className="absolute inset-0 flex items-center justify-center hover:bg-black/10 transition-colors"
                    aria-label="Play video"
                  >
                    <PlayButton />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA 按钮 */}
        <div className="flex justify-center">
          <StartButton />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
